import React, { useEffect, useRef, useState } from "react";
import { Button } from "Components/atoms/Button";
import { Radio } from "Components/atoms/Radio";
import { DataSearchDestinationDesktop } from "Components/molecules/DataSearch/Desktop/Destination";
import { DataSearchOriginDesktop } from "Components/molecules/DataSearch/Desktop/Origin";
import { DestinationFieldSearch } from "Components/molecules/FieldSearch/Desktop/Destination";
import { OriginFieldSearch } from "Components/molecules/FieldSearch/Desktop/Origin";
import { GuestSelectorWithInput } from "Components/molecules/GuestSelectorWithInput/Desktop";
import { useSearchContext } from "shared/contexts/SearchContext";
import { useClickOutside } from "shared/hooks/useClickOutside";
import {
  ContentWrapper,
  DatasSearchWrapper,
  Divisor,
  RadioWrapper,
  SearchContentWrapper,
} from "./styles";
import { CalendarProvider } from "shared/contexts/CalendarContext";
import { Airport } from "shared/types/airportsTypes";
import { IoIosSearch } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import altRedirectConfig from "shared/config/alt_redirect/alt-redirect.json";
import { AltRedirectConfig } from "shared/config/alt_redirect/types";
import { handleAltRedirect } from "shared/utils/handleAltRedirect";
import { sendGTMEvent } from "shared/services/GTM";
import { composeURL } from "shared/utils/urlComposer";
import { useAltRedirectHook } from "shared/services/altRedirect/useAltRedirectHook";
import { AltRedirectCampaigns } from "shared/services/altRedirect/types";
import { useBreakpoint } from "shared/hooks/useBreakpoint";
import { getEnvironment } from "@/shared/utils/getEnvironment";
import { captureSentryException } from "shared/services/sentry/captureException";

export const SearchFlightsDesktop: React.FC = () => {
  const {
    origins,
    nationalDestinations,
    internationalDestinations,
    searchState,
    setTripType,
    setOrigin,
    setDestination,
    setVisibility,
  } = useSearchContext();

  const navigate = useNavigate();
  const originRef = useRef<HTMLDivElement>(null);
  const destinationRef = useRef<HTMLDivElement>(null);

  const { altRedirectCampaign } = useAltRedirectHook<AltRedirectCampaigns>();

  const [isMobile, setMobile] = useState(false);
  const breakpoint = useBreakpoint();

  useEffect(() => {
    setMobile(breakpoint !== "xxl");
  }, [breakpoint]);

  useClickOutside([originRef, destinationRef], () => {
    if (searchState.isOriginVisible) setVisibility("origin", false);
    if (searchState.isDestinationVisible) setVisibility("destination", false);
  });

  const handleOpenOrigin = () => {
    setVisibility("destination", false);
    setVisibility("origin", true);
  };

  const handleOpenDestination = () => {
    setVisibility("origin", false);
    setVisibility("destination", true);
  };

  const handleSearch = async () => {
    if (
      !searchState.origin ||
      searchState.origin.code === "" ||
      !searchState.destination ||
      searchState.origin.code === "" ||
      !searchState.departureDates?.length
    ) {
      alert("Por favor, preencha todos os campos obrigatórios!");
      return;
    }

    const altRedirect = handleAltRedirect(
      searchState.origin.code,
      searchState.destination.code,
      altRedirectCampaign,
      isMobile,
      {
        departureDates: searchState.departureDates,
        returnDates: searchState.returnDates,
        adults: searchState.adults,
        children: searchState.children,
        babies: searchState.babies,
      },
    );

    const url = composeURL(searchState);

    if (altRedirect.execute) {
      window.open(url, "_blank");
      if (altRedirect.url) {
        setAltRedirectGTM(altRedirect?.name ?? "");
        window.location.href = altRedirect.url;
      }
    } else {
      window.open(url, "_self");
    }
  };

  const setAltRedirectGTM = (anunciante: string) => {
    try {
      const departureDates = searchState.departureDates || [];
      const returnDates = searchState.returnDates || [];

      const event = {
        anunciante: anunciante ?? "",
        airport_origin: searchState.origin?.code ?? "",
        airport_destination: searchState.destination?.code ?? "",
        flight_depart_date: departureDates,
        flight_return_date: returnDates,
        device: "desktop",
      };

      sendGTMEvent("alt_redirect", event);
    } catch (error) {
      captureSentryException(error, {
        tags: {
          feature: "search-flights",
          operation: "sendAltRedirectGTM",
          device: "desktop",
        },
      });
      console.error("Erro ao enviar evento alt_redirect ao GTM");
    }
  };

  return (
    <SearchContentWrapper id="flight-search">
      <RadioWrapper>
        <Radio
          name="options"
          value="roundTrip"
          label="Ida e volta"
          isSelected={searchState.tripType === "roundTrip"}
          onChange={setTripType}
        />
        <Radio
          name="options"
          value="oneWay"
          label="Só ida"
          isSelected={searchState.tripType === "oneWay"}
          onChange={setTripType}
        />
      </RadioWrapper>

      <ContentWrapper>
        <DatasSearchWrapper>
          <OriginFieldSearch
            ref={originRef}
            origins={origins}
            origin={searchState.origin?.description || ""}
            isVisible={searchState.isOriginVisible}
            setIsVisible={(isVisible) =>
              isVisible ? handleOpenOrigin() : setVisibility("origin", false)
            }
            onSelect={(origin: Airport) => setOrigin(origin)}
          />
          <Divisor />
          <DestinationFieldSearch
            ref={destinationRef}
            nationalDestinations={nationalDestinations}
            internationalDestinations={internationalDestinations}
            destination={searchState.destination?.description || ""}
            isVisible={searchState.isDestinationVisible}
            setIsVisible={(isVisible) =>
              isVisible
                ? handleOpenDestination()
                : setVisibility("destination", false)
            }
            onSelect={(destination: Airport) => setDestination(destination)}
          />
          <Divisor />
          <CalendarProvider>
            <DataSearchOriginDesktop />
            <Divisor />
            <DataSearchDestinationDesktop />
          </CalendarProvider>
          <Divisor />
          <GuestSelectorWithInput />
        </DatasSearchWrapper>

        <Button
          label="Buscar passagens"
          backgroundColor="yellow"
          color="black"
          size="small"
          icon={<IoIosSearch />}
          uppercase
          rounded
          onClick={handleSearch}
        />
      </ContentWrapper>
    </SearchContentWrapper>
  );
};
